import { create } from "zustand";
import { activitiesApi, ApiError, type DuplicateResponse } from "../lib/api";
import type { StoredActivity, ActivityListItem, SyncResult } from "@runlens/shared";

interface ActivitiesState {
  // Activities list
  activities: ActivityListItem[];
  total: number;
  loading: boolean;
  error: string | null;

  // Current activity details
  currentActivity: StoredActivity | null;
  currentActivityLoading: boolean;
  currentActivityError: string | null;

  // Upload state
  uploading: boolean;
  uploadError: string | null;
  uploadDuplicate: ActivityListItem | null;

  // Sync state
  syncing: boolean;
  syncResult: SyncResult | null;
  syncError: string | null;
  syncNeedsReconnect: boolean;
  syncRetryAfter: number | null;

  // Actions
  fetchActivities: (options?: { limit?: number; offset?: number }) => Promise<void>;
  fetchActivity: (activityId: string) => Promise<void>;
  updateActivity: (activityId: string, data: { name?: string }) => Promise<boolean>;
  uploadFile: (file: File) => Promise<boolean>;
  deleteActivity: (activityId: string) => Promise<boolean>;
  syncFromProvider: (provider: string) => Promise<SyncResult | null>;
  clearUploadState: () => void;
  clearSyncState: () => void;
  reset: () => void;
}

export const useActivitiesStore = create<ActivitiesState>((set, get) => ({
  // Initial state
  activities: [],
  total: 0,
  loading: false,
  error: null,

  currentActivity: null,
  currentActivityLoading: false,
  currentActivityError: null,

  uploading: false,
  uploadError: null,
  uploadDuplicate: null,

  syncing: false,
  syncResult: null,
  syncError: null,
  syncNeedsReconnect: false,
  syncRetryAfter: null,

  fetchActivities: async (options) => {
    set({ loading: true, error: null });

    try {
      const data = await activitiesApi.list(options);
      set({
        activities: data.activities,
        total: data.total,
        loading: false,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to fetch activities";
      set({ loading: false, error: message });
    }
  },

  fetchActivity: async (activityId: string) => {
    set({ currentActivityLoading: true, currentActivityError: null });

    try {
      const activity = await activitiesApi.get(activityId);
      set({
        currentActivity: activity,
        currentActivityLoading: false,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to fetch activity";
      set({
        currentActivityLoading: false,
        currentActivityError: message,
      });
    }
  },

  uploadFile: async (file: File) => {
    set({ uploading: true, uploadError: null, uploadDuplicate: null });

    try {
      const result = await activitiesApi.upload(file);

      if (result.isDuplicate) {
        const duplicateResult = result as DuplicateResponse;
        set({
          uploading: false,
          uploadDuplicate: duplicateResult.existingActivity,
        });
        return false;
      }

      // Add the new activity to the list
      const { activities } = get();
      set({
        uploading: false,
        activities: [result.activity, ...activities],
        total: get().total + 1,
      });
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to upload file";
      set({ uploading: false, uploadError: message });
      return false;
    }
  },

  updateActivity: async (activityId: string, data: { name?: string }) => {
    try {
      const updatedActivity = await activitiesApi.update(activityId, data);

      // Update in activities list
      const { activities } = get();
      set({
        activities: activities.map((a) =>
          a.id === activityId ? { ...a, name: updatedActivity.name } : a
        ),
      });

      // Update current activity if it's the one being updated
      if (get().currentActivity?.id === activityId) {
        set({ currentActivity: updatedActivity });
      }

      return true;
    } catch (error) {
      console.error("Failed to update activity:", error);
      return false;
    }
  },

  deleteActivity: async (activityId: string) => {
    try {
      await activitiesApi.delete(activityId);

      // Remove from list
      const { activities } = get();
      set({
        activities: activities.filter((a) => a.id !== activityId),
        total: get().total - 1,
      });

      // Clear current if it was the deleted one
      if (get().currentActivity?.id === activityId) {
        set({ currentActivity: null });
      }

      return true;
    } catch (error) {
      console.error("Failed to delete activity:", error);
      return false;
    }
  },

  syncFromProvider: async (provider: string) => {
    set({ syncing: true, syncError: null, syncResult: null, syncNeedsReconnect: false, syncRetryAfter: null });

    try {
      const result = await activitiesApi.sync(provider);
      set({
        syncing: false,
        syncResult: result,
      });

      // Refresh activities list after sync
      await get().fetchActivities();

      return result;
    } catch (error) {
      // Rate limit — show friendly message with retry info
      if (error instanceof ApiError && error.status === 429) {
        set({ syncing: false, syncError: error.message, syncRetryAfter: 900 });
        return null;
      }

      const message = error instanceof Error ? error.message : "Sync failed";
      // Check if error indicates reconnection is needed
      const needsReconnect = message.includes("reconnect") ||
                             message.includes("expired") ||
                             message.includes("Session");
      set({ syncing: false, syncError: message, syncNeedsReconnect: needsReconnect });
      return null;
    }
  },

  clearUploadState: () => {
    set({ uploadError: null, uploadDuplicate: null });
  },

  clearSyncState: () => {
    set({ syncResult: null, syncError: null, syncNeedsReconnect: false, syncRetryAfter: null });
  },

  reset: () => {
    set({
      activities: [],
      total: 0,
      loading: false,
      error: null,
      currentActivity: null,
      currentActivityLoading: false,
      currentActivityError: null,
      uploading: false,
      uploadError: null,
      uploadDuplicate: null,
      syncing: false,
      syncResult: null,
      syncError: null,
      syncNeedsReconnect: false,
      syncRetryAfter: null,
    });
  },
}));
